import {
  type ReengagementConfig,
  DEFAULT_REENGAGEMENT_CONFIG,
} from '@/lib/api/botSettings'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'
import {
  RotateCcw,
  Clock,
  Plus,
  Trash2,
  HelpCircle,
  CheckCircle2,
  AlertCircle,
  MessageSquare,
} from 'lucide-react'

interface ReengagementConfigCardProps {
  config: ReengagementConfig
  onChange: (next: ReengagementConfig) => void
  disabled?: boolean
}

export default function ReengagementConfigCard({
  config,
  onChange,
  disabled = false,
}: ReengagementConfigCardProps) {
  const handleToggle = (checked: boolean) => {
    onChange({
      ...config,
      enabled: checked,
    })
  }

  const handleTimeoutChange = (minutes: number) => {
    const clamped = Math.min(1440, Math.max(5, isNaN(minutes) ? 20 : minutes))
    onChange({
      ...config,
      timeoutMinutes: clamped,
    })
  }

  const handleMaxAttemptsChange = (attempts: number) => {
    const clamped = Math.min(3, Math.max(1, attempts))
    onChange({
      ...config,
      maxPerConversation: clamped,
    })
  }

  const handleOnlyPriceToggle = (checked: boolean) => {
    onChange({
      ...config,
      onlyAfterPriceInquiry: checked,
    })
  }

  const handleMessageChange = (index: number, text: string) => {
    const updated = [...config.messages]
    updated[index] = text.slice(0, 200)
    onChange({
      ...config,
      messages: updated,
    })
  }

  const handleAddMessage = () => {
    if (config.messages.length >= 5) return
    const updated = [
      ...config.messages,
      '¿Sigues interesado en este producto? Con gusto te ayudo a coordinar tu pedido 😊',
    ]
    onChange({
      ...config,
      messages: updated,
    })
  }

  const handleRemoveMessage = (index: number) => {
    if (config.messages.length <= 1) return
    const updated = config.messages.filter((_, i) => i !== index)
    onChange({
      ...config,
      messages: updated,
    })
  }

  const handleRestoreDefaults = () => {
    onChange({
      ...config,
      messages: [...DEFAULT_REENGAGEMENT_CONFIG.messages],
    })
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-200/90 shadow-sm overflow-hidden transition-all duration-200">
      {/* Encabezado con Switch Principal */}
      <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-gray-50/50 to-white">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3.5">
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-600 flex items-center justify-center flex-shrink-0 mt-0.5">
              <RotateCcw className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base font-bold text-gray-900">
                  Recuperación Automática de Leads
                </h3>
                <span className="px-2 py-0.5 text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full">
                  Ventas Perdidas
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1 max-w-xl leading-relaxed">
                Recontacta proactivamente a clientes que dejaron de responder tras recibir un precio o cotización, evitando que la venta se enfríe.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-xs font-semibold text-gray-700 hidden sm:inline">
              {config.enabled ? 'Activado' : 'Desactivado'}
            </span>
            <Switch
              checked={config.enabled}
              onChange={(e) => handleToggle(e.target.checked)}
              disabled={disabled}
              aria-label="Activar recuperación automática de leads"
            />
          </div>
        </div>

        {!config.enabled && (
          <p className="text-xs text-gray-500 mt-3 pt-3 border-t border-gray-100 flex items-center gap-1.5">
            <HelpCircle className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            Por defecto desactivado. Al activarlo, el bot enviará un mensaje de seguimiento si el cliente queda en silencio.
          </p>
        )}
      </div>

      {/* Contenido Configurable cuando está habilitado */}
      {config.enabled && (
        <div className="p-6 space-y-6">
          {/* Banner explicativo */}
          <div className="bg-amber-50/60 border border-amber-200/80 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-amber-900 leading-relaxed">
              <p className="font-semibold text-amber-950">Seguimiento inteligente y seguro</p>
              El bot esperará el lapso configurado en silencio. Si el cliente responde por su cuenta, compra o es transferido a un humano, el seguimiento se cancela de inmediato automáticamente.
            </div>
          </div>

          {/* Fila de Parámetros: Tiempo y Máximo de Intentos */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Tiempo de espera */}
            <div className="bg-gray-50/60 border border-gray-200/70 rounded-xl p-4 space-y-2">
              <label
                htmlFor="reengagement-timeout"
                className="flex items-center gap-2 text-xs font-bold text-gray-800"
              >
                <Clock className="w-3.5 h-3.5 text-gray-500" />
                Tiempo de espera tras el precio
              </label>
              <div className="flex items-center gap-3">
                <input
                  id="reengagement-timeout"
                  type="number"
                  min={5}
                  max={1440}
                  value={config.timeoutMinutes}
                  onChange={(e) => handleTimeoutChange(parseInt(e.target.value, 10))}
                  disabled={disabled}
                  className="w-24 h-9 px-3 text-sm font-semibold rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#075E54] focus:border-transparent"
                />
                <span className="text-xs font-medium text-gray-600">minutos de silencio</span>
              </div>
              <p className="text-[11px] text-gray-500 leading-normal">
                Recomendado: 20 minutos. Rango permitido: entre 5 minutos y 24 horas (1440 min).
              </p>
            </div>

            {/* Máximo de intentos */}
            <div className="bg-gray-50/60 border border-gray-200/70 rounded-xl p-4 space-y-2">
              <label
                htmlFor="reengagement-max-attempts"
                className="flex items-center gap-2 text-xs font-bold text-gray-800"
              >
                <CheckCircle2 className="w-3.5 h-3.5 text-gray-500" />
                Máximo de intentos por conversación
              </label>
              <select
                id="reengagement-max-attempts"
                value={config.maxPerConversation}
                onChange={(e) => handleMaxAttemptsChange(parseInt(e.target.value, 10))}
                disabled={disabled}
                className="w-full h-9 px-3 text-sm font-medium rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#075E54] focus:border-transparent"
              >
                <option value={1}>1 intento (muy discreto)</option>
                <option value={2}>2 intentos (recomendado)</option>
                <option value={3}>3 intentos (seguimiento intensivo)</option>
              </select>
              <p className="text-[11px] text-gray-500 leading-normal">
                Tope máximo de mensajes de reactivación para no cansar al cliente si no responde.
              </p>
            </div>
          </div>

          {/* Filtro: Solo tras consulta de precio */}
          <div className="flex items-start gap-3 p-3.5 bg-gray-50/60 border border-gray-200/70 rounded-xl">
            <input
              id="reengagement-only-price"
              type="checkbox"
              checked={config.onlyAfterPriceInquiry}
              onChange={(e) => handleOnlyPriceToggle(e.target.checked)}
              disabled={disabled}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#075E54] focus:ring-[#075E54]"
            />
            <label htmlFor="reengagement-only-price" className="text-xs text-gray-700 cursor-pointer select-none">
              <span className="font-bold text-gray-800 block">Solo contactar si se cotizó precio o producto</span>
              <span className="text-gray-500 block text-[11px] mt-0.5">
                Si está marcado, solo se reactivará al cliente si el bot le dio un precio o cotización. Si se desmarca, también reactivará estancamientos generales en descubrimiento.
              </span>
            </label>
          </div>

          {/* Mensajes personalizables */}
          <div className="space-y-3 pt-2">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-xs font-bold text-gray-800 flex items-center gap-1.5">
                  <MessageSquare className="w-3.5 h-3.5 text-gray-500" />
                  Mensajes de reactivación ({config.messages.length}/5)
                </h4>
                <p className="text-[11px] text-gray-500 mt-0.5">
                  El bot elegirá uno al azar para variar la comunicación. Puedes usar <code className="bg-gray-100 px-1 py-0.5 rounded text-gray-800 font-mono text-[10px]">&#123;producto&#125;</code> para mencionar el artículo consultado.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleRestoreDefaults}
                  disabled={disabled}
                  className="text-xs text-gray-600 hover:text-gray-800 h-8 px-2.5"
                >
                  Restaurar predeterminados
                </Button>
                {config.messages.length < 5 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddMessage}
                    disabled={disabled}
                    className="text-xs text-[#075E54] hover:text-[#064E46] h-8 px-2.5 flex items-center gap-1"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Añadir mensaje
                  </Button>
                )}
              </div>
            </div>

            {/* Lista de mensajes */}
            <div className="space-y-2.5">
              {config.messages.map((msg, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-2 p-2.5 bg-gray-50/70 border border-gray-200/80 rounded-xl focus-within:border-[#075E54] focus-within:ring-1 focus-within:ring-[#075E54] transition-all"
                >
                  <span className="text-[11px] font-bold text-gray-400 w-5 text-center flex-shrink-0">
                    {idx + 1}
                  </span>
                  <input
                    type="text"
                    value={msg}
                    maxLength={200}
                    onChange={(e) => handleMessageChange(idx, e.target.value)}
                    disabled={disabled}
                    placeholder="Escribe el mensaje de seguimiento..."
                    className="flex-1 bg-transparent text-xs text-gray-800 placeholder-gray-400 focus:outline-none"
                    aria-label={`Mensaje de reactivación ${idx + 1}`}
                  />
                  <span className="text-[10px] text-gray-400 flex-shrink-0 px-1">
                    {msg.length}/200
                  </span>
                  {config.messages.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveMessage(idx)}
                      disabled={disabled}
                      aria-label={`Eliminar mensaje ${idx + 1}`}
                      className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
